import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { useEffect, useCallback, useState, useRef } from "react";
import { useMessageSending } from "@/hooks/useMessageSending";
import { useApplyTemplateMutation, useGetMoveQuery } from "@/store/moves/apis";
import { MailIcon, MessageSquareText, SendIcon } from "lucide-react";
import { Customer } from "@/types";
import { Message } from "@/types";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getSelectedMover } from "@/store/mover/slice";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { MessageAttachments } from "./MessageAttachments";

interface ConversationMessagesProps {
  messages: Message[];
  customer: Customer;
  moveId?: number;
  onMessageSent?: () => void;
  showHeader?: boolean;
}

export const ConversationMessages = ({
  messages,
  customer,
  moveId,
  onMessageSent,
  showHeader = true,
}: ConversationMessagesProps) => {
  const mover = useSelector(getSelectedMover);
  const { handleSendEmail, handleSendText, isSending } = useMessageSending();
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [messageType, setMessageType] = useState("text");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { data: move } = useGetMoveQuery(moveId);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [populateTemplate] = useApplyTemplateMutation();

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "instant" });
    }
  }, []);

  console.log(mover?.templates);

  useEffect(() => {
    if (messages) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  const applyTemplate = async (templateId: string) => {
    const template = mover?.templates.find(
      (t) => t.id?.toString() === templateId
    );

    if (!template) {
      return;
    }

    try {
      const { data } = await populateTemplate({
        id: moveId,
        templateId,
      });

      const { subject, body } = data;

      setSubject(subject);
      setMessage(body);
      setSelectedTemplateId("");
    } catch (e) {
      console.error(e);
      toast.error("Failed to apply template");
    }
  };

  const handleSend = async () => {
    if (messageType === "text") {
      await handleSendText(customer.id, message, moveId);
    } else {
      await handleSendEmail(customer.id, subject, message, moveId);
    }

    setMessage("");
    setSubject("");
    onMessageSent?.();
  };

  const renderMessage = (message: Message) => {
    if (message.type === "email") {
      return <div dangerouslySetInnerHTML={{ __html: message.body }} />;
    }

    return <p>{message.body}</p>;
  };

  if (!move) return null;
  if (!mover) return null;

  return (
    <div className="flex flex-col h-full w-full overflow-none">
      {showHeader && (
        <div className="px-2 py-3 bg-white border-b border-black -mr-4 z-[1]">
          <Link
            className="underline underline-offset-4 decoration-yellow-500"
            target="_blank"
            to={`/move/${move.id}`}
          >
            {customer.name}
          </Link>
        </div>
      )}
      <ScrollArea className="flex flex-col h-full w-full px-4 pt-4">
        {messages.map((message) => (
          <>
            <div
              className={cn(
                "text-sm text-black flex justify-start items-center gap-1",
                (message.sender_type === "mover" ||
                  message.sender_type === "system") &&
                  "ml-12 font-semibold",
                message.sender_type === "customer" &&
                  "text-blue-900 font-semibold"
              )}
            >
              {message.type === "email" ? (
                <MailIcon className="w-4 h-4" />
              ) : (
                <MessageSquareText className="w-4 h-4" />
              )}
              <div>
                On {format(parseISO(message.created_at), "MMM d, yyyy h:mm a")}
              </div>
              <div>
                {message.sender_type === "customer" &&
                  `${customer.name} ${
                    message.type === "email" ? "sent an email" : "sent a text"
                  }`}
                {message.sender_type === "mover" &&
                  `${message.sending_user_name || "you"} ${
                    message.type === "email" ? "sent an email" : "sent a text"
                  }`}
                {message.sender_type === "system" && "automated message"}
              </div>
            </div>
            <div
              key={message.id}
              className={cn(
                "flex flex-col w-full lg:w-2/3 mt-2 mb-8 py-1",
                message.sender_type === "customer" && "bg-white",
                message.sender_type === "mover" && "text-black",
                message.sender_type === "system" && "bg-white text-black",
                (message.sender_type === "mover" ||
                  message.sender_type === "system") &&
                  "ml-12"
              )}
            >
              <div
                className={cn(
                  "rounded-sm shadow-none",
                  message.sender_type === "customer" ? "text-black" : ""
                )}
              >
                {renderMessage(message)}
                <MessageAttachments message={message} />
              </div>
            </div>
          </>
        ))}
        <div ref={messagesEndRef} />
      </ScrollArea>

      <div className="w-full px-4 py-2 border-t border-stone-700">
        <div className="flex justify-between items-baseline">
          <div>
            <ToggleGroup
              type="single"
              className="space-x-2"
              value={messageType}
              onValueChange={(value) => {
                if (value) {
                  setMessageType(value);
                  if (value === "email") scrollToBottom();
                }
              }}
            >
              <ToggleGroupItem
                value="text"
                aria-label="Text Message"
                className="data-[state=on]:bg-transparent data-[state=on]:text-blue-700 px-0 py-0 data-[state=on]:border-none data-[state=off]:bg-transparent data-[state=off]:text-black data-[state=off]:border-none flex items-center justify-center gap-1"
              >
                <MessageSquareText className="w-4 h-4" />
                <span>Text</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="email"
                aria-label="Email"
                className="data-[state=on]:bg-transparent data-[state=on]:text-blue-700 px-0 py-0 data-[state=on]:border-none data-[state=off]:bg-transparent data-[state=off]:text-black data-[state=off]:border-none flex items-center justify-center gap-1"
              >
                <MailIcon className="w-4 h-4" />
                <span>Email</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="flex items-center gap-4">
            <Select
              value={selectedTemplateId}
              onValueChange={(value) => {
                setSelectedTemplateId(value);
                applyTemplate(value);
              }}
            >
              <SelectTrigger className="">
                <SelectValue placeholder="Paste template" />
              </SelectTrigger>
              <SelectContent>
                {mover.templates
                  .filter((template) => template.channel === messageType)
                  .map((template) => (
                    <SelectItem
                      key={template.id}
                      value={template.id?.toString() || ""}
                    >
                      {template.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              variant="default"
              size="sm"
              className="flex items-center gap-2"
              onClick={handleSend}
              disabled={isSending}
            >
              <SendIcon className="w-4 h-4" />
              Send
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {messageType === "text" ? (
            <div className="flex flex-col gap-2 mt-2 w-full">
              <Textarea
                value={message}
                className="min-h-[140px]"
                placeholder="Message"
                onChange={(e) => setMessage(e.target.value)}
                onInput={(e) => {
                  const textarea = e.target as HTMLTextAreaElement;
                  textarea.style.height = "140px";
                  if (textarea.scrollHeight > 300) {
                    textarea.style.height = "300px";
                  } else {
                    textarea.style.height = `${textarea.scrollHeight}px`;
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 mt-2 w-full">
              <Input
                value={subject}
                placeholder="Subject"
                onChange={(e) => setSubject(e.target.value)}
              />
              <div
                contentEditable
                className="min-h-[140px] max-h-[300px] overflow-y-auto p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 prose prose-sm"
                ref={(el) => {
                  if (el) {
                    if (el.innerHTML !== message) {
                      el.innerHTML = message;
                    }
                  }
                }}
                onInput={(e) => {
                  const content = (e.target as HTMLDivElement).innerHTML;
                  setMessage(content);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const text =
                    e.clipboardData.getData("text/html") ||
                    e.clipboardData.getData("text");
                  document.execCommand("insertHTML", false, text);
                }}
              />
            </div>
          )}
        </div>
        <div className="text-sm mt-2 font-semibold text-stone-700">
          {messageType === "text"
            ? `Sending text to ${move?.customer_mobile_phone}`
            : `Sending email to ${move?.customer_email}`}
        </div>
      </div>
    </div>
  );
};
